import {
  useRef,
  type CSSProperties,
  type DragEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Upload, type LucideIcon } from "lucide-react";
import { Spinner } from "@/components/ui";
import { cn } from "@/lib/utils";

/**
 * Shared file picker with drag-and-drop.
 *
 * Intentional raw `<input type="file">` exceptions (compact attach UX):
 * - `MessengerChatComposer` — paperclip button + multi-file staging bar
 */
export interface FileDropzoneProps {
  id: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  visible?: boolean;
  className?: string;
  style?: CSSProperties;
  icon?: LucideIcon;
  iconClassName?: string;
  title?: ReactNode;
  description?: ReactNode;
  titleClassName?: string;
  isLoading?: boolean;
  /** Highlights the dropzone after a file has been chosen. */
  isSelected?: boolean;
  /** Stack (default) or horizontal row for button-like targets. */
  layout?: "stack" | "inline";
  spinnerSize?: "sm" | "md";
  onFilesSelected: (files: File[]) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  resetInputOnSelect?: boolean;
}

export function FileDropzone({
  id,
  accept,
  multiple = false,
  disabled = false,
  visible = true,
  className,
  style,
  icon: Icon = Upload,
  iconClassName,
  title,
  description,
  titleClassName,
  isLoading = false,
  isSelected = false,
  layout = "stack",
  spinnerSize = "md",
  onFilesSelected,
  inputRef,
  resetInputOnSelect = true,
}: FileDropzoneProps) {
  const { t } = useTranslation("common");
  const localInputRef = useRef<HTMLInputElement>(null);
  const resolvedInputRef = inputRef ?? localInputRef;
  const isDisabled = disabled || isLoading;
  const showText = title != null || description != null;
  const ResolvedIcon = isSelected && !isLoading ? CheckCircle2 : Icon;

  const applyFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) {
      return;
    }
    onFilesSelected(Array.from(fileList));
    if (resetInputOnSelect && resolvedInputRef.current) {
      resolvedInputRef.current.value = "";
    }
  };

  const handleDragOver = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (isDisabled) {
      return;
    }
    applyFiles(event.dataTransfer.files);
  };

  return (
    <>
      <input
        ref={resolvedInputRef}
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        className="sr-only"
        disabled={isDisabled}
        onChange={(event) => applyFiles(event.target.files)}
      />
      <label
        htmlFor={id}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={style}
        className={cn(
          "flex cursor-pointer items-center justify-center rounded-xl border-2 border-dashed",
          "text-center transition-colors",
          isSelected
            ? "border-primary-500 bg-primary-50 hover:border-primary-600 hover:bg-primary-50"
            : "border-surface-300 bg-surface-50 hover:border-primary-400 hover:bg-primary-50/40",
          layout === "stack" && "flex-col gap-3 px-6 py-9",
          layout === "inline" && "flex-row gap-2 px-3 py-2",
          isDisabled && "pointer-events-none opacity-60",
          !visible && "hidden",
          className,
        )}
      >
        {isLoading ? (
          <Spinner size={spinnerSize} loadingLabel={t("loading")} />
        ) : (
          <ResolvedIcon
            className={cn(
              layout === "stack" ? "h-8 w-8" : "h-4 w-4",
              isSelected ? "text-primary-600" : "text-surface-400",
              iconClassName,
            )}
            aria-hidden
          />
        )}
        {showText ? (
          <div className={cn("space-y-1", layout === "inline" && "text-left")}>
            {title != null ? (
              <div
                className={cn(
                  layout === "stack" && "text-sm font-medium",
                  isSelected ? "text-primary-900" : "text-surface-800",
                  titleClassName,
                )}
              >
                {title}
              </div>
            ) : null}
            {description ? (
              <p
                className={cn(
                  "text-xs",
                  isSelected ? "text-primary-700" : "text-surface-500",
                )}
              >
                {description}
              </p>
            ) : null}
          </div>
        ) : null}
      </label>
    </>
  );
}
